import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { useState } from "react";

import { useCoachingGeneratorStore } from "../store/coachingGeneratorStore";
import { AttributeExamples } from "../types";
import AttributeCardSummary from "./AttributeCardSummary";
import ExamplesList from "./ExamplesList";

interface CoachingAttributeCardProps {
  attributeExamples: AttributeExamples;
}

type ExampleType = "low" | "high" | null;

const CoachingAttributeCard = ({ attributeExamples }: CoachingAttributeCardProps) => {
  const {
    selectedAttributes,
    expandedAttribute,
    toggleAttribute,
    setExpandedAttribute
  } = useCoachingGeneratorStore();

  const [expandedType, setExpandedType] = useState<ExampleType>(null);

  const isSelected = selectedAttributes.has(attributeExamples.attribute);
  const isExpanded = expandedAttribute === attributeExamples.attribute;

  const handleExampleTypeClick = (type: ExampleType) => (event: React.MouseEvent) => {
    event.stopPropagation();
    if (expandedType === type) {
      setExpandedType(null);
      setExpandedAttribute(null);
    } else {
      setExpandedType(type);
      setExpandedAttribute(attributeExamples.attribute);
    }
  };

  const handleAccordionChange = (_event: React.SyntheticEvent, expanded: boolean) => {
    if (!expanded) {
      setExpandedType(null);
      setExpandedAttribute(null);
    }
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleAccordionChange}
      sx={{
        mb: 1,
        boxShadow: 1,
        borderRadius: 1,
        "&:before": {
          display: "none"
        },
        "&.Mui-expanded": {
          margin: "0 0 8px 0",
          boxShadow: 1
        }
      }}
    >
      <AccordionSummary
        sx={{
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            gap: 2,
            my: 1.5
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            display: "none"
          }
        }}
      >
        <AttributeCardSummary
          attributeExamples={attributeExamples}
          isSelected={isSelected}
          onSelect={toggleAttribute}
          isExpanded={isExpanded}
          onExpand={(attribute) => {
            if (attribute === null) {
              setExpandedType(null);
            }
            setExpandedAttribute(attribute);
          }}
          expandedType={expandedType}
          onExampleTypeClick={handleExampleTypeClick}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {expandedType === "low" && <ExamplesList examples={attributeExamples.lowScoring} type="low" />}
        {expandedType === "high" && <ExamplesList examples={attributeExamples.highScoring} type="high" />}
      </AccordionDetails>
    </Accordion>
  );
};

export default CoachingAttributeCard;
